/**
 * Home page idle warm-up: after the first screen has loaded, download all remaining loading="lazy" images ahead of time in one go.
 *
 * Why this is needed (measured 2026-09-18):
 *   · From mainland China, every request to Shopify spends ~0.7s just on connection setup + first byte (TLS 0.5s), regardless of file size --
 *     a 1KB svg and a 96KB hero image both take ~1.1s to arrive. Native lazy loading only starts ~1250px ahead,
 *     so the image is still in flight when the user scrolls up to it, which looks like "blank, then pops in" and was reported as jank.
 *   · In the scenes carousel (soulog-home-scenes), cards 3 and 4 are clipped inside a horizontal overflow;
 *     native lazy loading can't tell they are "about to be visible", so they only start downloading once the card slides in -- an empty card slides in.
 *   · After the desktop home page's first load, the remaining lazy images total only ~280KB, so downloading them all early costs very little.
 *
 * Approach: on load (or 3s after DOMContentLoaded, whichever comes first) -> when idle, switch loading to eager for images within
 * AHEAD_SCREENS viewports below the current one, and keep doing that as the user scrolls (rAF-throttled, listener removed once
 * nothing is left). The first-screen hero image is already eager + fetchpriority=high, so it is unaffected.
 *
 * 2026-09-27: it used to switch **every** lazy image on the page at once. The new home page has far more images below the fold
 * (color switch 20 angle shots, shop module 12 large shots…): measured 67 images / 1.6MB pulled on phones without scrolling.
 * 3 screens is still well beyond native lazy loading's ~1250px look-ahead, so the "blank, then pops in" fix is kept;
 * horizontally clipped carousel cards are in the band by their vertical position, so those are still warmed too.
 *
 * Skipped:
 *   · images with no layout box (desktop-only/mobile-only images that are display:none) -- switching them to eager would waste a download on an unused image;
 *   · users with data saver (saveData) on or on a 2g network -- keep native lazy loading.
 */
(function () {
	"use strict";

	if (window.__soulogImageWarmup) return;
	window.__soulogImageWarmup = true;

	var conn = navigator.connection;
	if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ""))) return;

	// Maximum extra wait after DOMContentLoaded: preview/third-party scripts can push the load event past 9s,
	// by which point the user has long since started scrolling and warming up is pointless
	var FALLBACK_MS = 3000;
	// How far ahead to warm, in viewport heights below the current viewport
	var AHEAD_SCREENS = 3;

	/** Warm images inside the band; returns how many lazy images with a layout box are still left outside it */
	function warm(root) {
		var scope = root && root.querySelectorAll ? root : document;
		var imgs = scope.querySelectorAll('img[loading="lazy"]');
		var vh = window.innerHeight || 800;
		var left = 0;
		for (var i = 0; i < imgs.length; i++) {
			var img = imgs[i];
			if (!img.getClientRects().length) continue;
			var rect = img.getBoundingClientRect();
			if (rect.bottom < -vh || rect.top > vh * (1 + AHEAD_SCREENS)) {
				left++;
				continue;
			}
			img.loading = "eager";
		}
		return left;
	}

	var queued = false;
	function onScroll() {
		if (queued) return;
		queued = true;
		window.requestAnimationFrame(function () {
			queued = false;
			if (!warm(document)) window.removeEventListener("scroll", onScroll);
		});
	}

	var started = false;
	function start() {
		if (started) return;
		started = true;
		var idle =
			window.requestIdleCallback ||
			function (fn) {
				return setTimeout(fn, 200);
			};
		idle(
			function () {
				if (warm(document)) window.addEventListener("scroll", onScroll, { passive: true });
			},
			{ timeout: 2000 },
		);
	}

	if (document.readyState === "complete") {
		start();
	} else {
		window.addEventListener("load", start, { once: true });
		var armFallback = function () {
			setTimeout(start, FALLBACK_MS);
		};
		if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", armFallback, { once: true });
		else armFallback();
	}

	// Sections re-rendered in the theme editor: the new nodes are lazy again, so run once more
	document.addEventListener("shopify:section:load", function (e) {
		if (started) warm(e.target);
	});
})();
